'use client';

import { c as _c } from "react/compiler-runtime";
import { CollectionsProvider } from '@/components/CollectionsContext';
import { QueryProvider } from '@sizzlenz/shared-frontend/query';
import { AuthSessionProvider } from '@/components/auth/AuthSessionProvider';
import { ModalProvider, ModalRenderer, SharedUIProvider, SheetManagerProvider, registerSignInComponent } from '@sizzlenz/shared-frontend/ui';
import { ModalShell, SheetShell } from '@/components/ui/shared-ui-adapter';
import { ModalContextInitializer } from '@/components/ui/modal-context-initializer';
import { KeyboardShortcutsProvider, LocalSettingsProvider, SessionStateProvider } from '@sizzlenz/shared-frontend/providers';
import { ReactionDialogProvider } from '@/lib/context/ReactionDialogContext';
import { ReactionDialogManager } from '@/components/activities/ReactionDialogManager';
import { SignIn } from '@/components/auth/SignIn';
import ScrollRestoration from '@/components/ScrollRestoration';
import { PostHogProvider as PHProvider, usePostHog } from '@posthog/react';
import { UserbackProvider, useUserback } from '@userback/react';
import { useEffect, useRef } from 'react';
import { Toaster } from './ui/sonner';
import { useSession } from '@/lib/auth-client';
function PostHogProvider(t0) {
  const $ = _c(17);
  const {
    children
  } = t0;
  const posthog = usePostHog();
  const {
    data: session,
    isPending
  } = useSession();
  let t1;
  if ($[0] !== posthog) {
    t1 = () => {
      if (process.env.NEXT_PUBLIC_POSTHOG_ENABLED == "true") {
        posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
          api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://e.sizzle.nz",
          defaults: "2025-11-30",
          enable_recording_console_log: true
        });
      }
    };
    $[0] = posthog;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== isPending || $[4] !== posthog || $[5] !== session) {
    t3 = () => {
      if (!isPending && session?.user) {
        posthog.identify(`user-${session.user.id}`, {
          email: session.user.email,
          name: session.user.name
        });
      }
    };
    $[3] = isPending;
    $[4] = posthog;
    $[5] = session;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = session?.user?.id;
  const t5 = session?.user?.email;
  const t6 = session?.user?.name;
  let t7;
  if ($[7] !== isPending || $[8] !== t4 || $[9] !== t5 || $[10] !== t6) {
    t7 = [t4, isPending, t5, t6];
    $[7] = isPending;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  useEffect(t3, t7);
  if (process.env.NEXT_PUBLIC_POSTHOG_ENABLED != "true") {
    let t8;
    if ($[12] !== children) {
      t8 = <>{children}</>;
      $[12] = children;
      $[13] = t8;
    } else {
      t8 = $[13];
    }
    return t8;
  }
  let t8;
  if ($[14] !== children || $[15] !== posthog) {
    t8 = <PHProvider client={posthog}>{children}</PHProvider>;
    $[14] = children;
    $[15] = posthog;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
}
function UserbackIdentify() {
  const $ = _c(9);
  const {
    data: session
  } = useSession();
  const hasIdentified = useRef(false);
  const userback = useUserback();
  let t0;
  if ($[0] !== session) {
    t0 = session?.user ? {
      id: String(session.user.id),
      info: {
        name: session.user.name || undefined,
        email: session.user.email || undefined
      }
    } : undefined;
    $[0] = session;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const userData = t0;
  let t1;
  if ($[2] !== session || $[3] !== userData || $[4] !== userback) {
    t1 = () => {
      if (!hasIdentified.current && session?.user?.id && userback) {
        userback.identify(session.user.id, userData.info);
        hasIdentified.current = true;
      }
    };
    $[2] = session;
    $[3] = userData;
    $[4] = userback;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const t2 = session?.user?.id;
  let t3;
  if ($[6] !== t2 || $[7] !== userback) {
    t3 = [t2, userback];
    $[6] = t2;
    $[7] = userback;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  useEffect(t1, t3);
  return null;
}
function UserbackWrapper(t0) {
  const $ = _c(6);
  const {
    children
  } = t0;
  const token = process.env.NEXT_PUBLIC_USERBACK_TOKEN;
  const isEmbed = typeof window !== "undefined" && new URLSearchParams(window.location.search).has("embed");
  if (!token || isEmbed) {
    let t1;
    if ($[0] !== children) {
      t1 = <>{children}</>;
      $[0] = children;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      on_open: _temp,
      on_close: _temp2
    };
    t2 = <UserbackIdentify />;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  let t3;
  if ($[4] !== children) {
    t3 = <UserbackProvider token={token} options={t1}>{t2}{children}</UserbackProvider>;
    $[4] = children;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}

// Tell the shared modal helpers which sign-in component to render. Done at module
// scope so `showSignInModal()` works even when called outside the React tree.
function _temp2() {
  const meta_0 = document.querySelector("meta[name=\"viewport\"]");
  if (meta_0) {
    meta_0.setAttribute("content", "width=device-width, initial-scale=1, interactive-widget=resizes-content");
  }
}
function _temp() {
  const meta = document.querySelector("meta[name=\"viewport\"]");
  if (meta) {
    meta.setAttribute("content", "width=device-width, initial-scale=1, interactive-widget=overlays-content");
  }
}
registerSignInComponent(SignIn);
export function Providers(t0) {
  const $ = _c(9);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ScrollRestoration />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Toaster />;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ModalContextInitializer />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== children) {
    t4 = <UserbackWrapper>{children}</UserbackWrapper>;
    $[3] = children;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ModalRenderer />;
    t6 = <ReactionDialogManager />;
    $[5] = t5;
    $[6] = t6;
  } else {
    t5 = $[5];
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t4) {
    t7 = <QueryProvider><SessionStateProvider>{t1}<LocalSettingsProvider><KeyboardShortcutsProvider><SharedUIProvider ModalShell={ModalShell} SheetShell={SheetShell}><ModalProvider>{t2}<PostHogProvider><AuthSessionProvider><ReactionDialogProvider><CollectionsProvider><SheetManagerProvider>{t3}{t4}{t5}{t6}</SheetManagerProvider></CollectionsProvider></ReactionDialogProvider></AuthSessionProvider></PostHogProvider></ModalProvider></SharedUIProvider></KeyboardShortcutsProvider></LocalSettingsProvider></SessionStateProvider></QueryProvider>;
    $[7] = t4;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  return t7;
}